import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, ArrowRight, Gift } from 'lucide-react';
import { Button } from './ui/Button';
import { Container } from './ui/Container';
import { trackAddToCart } from '../utils/metaPixel';
const IMAGES = {
  hero: 'https://cdn.magicpatterns.com/uploads/cES34sjBtDAJteDaKkFSUr/imgi_15_1747183934-Untitled_design_(19).png',
  packshot: 'https://cdn.magicpatterns.com/uploads/ckEK2fDVMho2aEzwy1jUxB/imgi_30_1746975295-SnoreStop™_(1).png',
  peaceful: 'https://cdn.magicpatterns.com/uploads/ucyfC3msYKWzV4hpVgap4d/imgi_17_1746975327-4.png'
};
const CHECKOUT_URLS = {
  pack1: 'http://glowliftessence.com/cart/45812197982398:1',
  pack3: 'http://glowliftessence.com/cart/45812198015166:1',
  pack5: 'http://glowliftessence.com/cart/45812198047934:1'
};
export function HeroSection() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handlePurchaseClick = (pack: '1x' | '3x' | '5x', price: number, url: string) => {
    trackAddToCart(`SnoreStop™ ${pack} Pack`, price);
    window.location.href = url;
  };
  return <section className="relative bg-gradient-to-b from-blue-50 to-white pt-8 pb-16 overflow-hidden" data-id="element-163">
      <Container data-id="element-164">
        {/* Mobile Header Logo */}
        <div className="flex justify-center mb-8 md:hidden" data-id="element-165">
          <img src="https://cdn.magicpatterns.com/uploads/j7d4EdH3Rp1gzCoto8E26y/imgi_1_1746975280-SnoreStop™.png" alt="SnoreStop Logo" className="h-10" data-id="element-166" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-167">
          {/* Left Content */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="space-y-6" data-id="element-168">
            {/* Trust Badge */}
            <div className="flex items-center space-x-2 bg-blue-100 w-fit px-4 py-1.5 rounded-full text-blue-900 text-sm font-bold tracking-wide" data-id="element-169">
              <div className="flex" data-id="element-170">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3.5 h-3.5 fill-current text-orange-500" data-id="element-171" />)}
              </div>
              <span data-id="element-172">#1 RATED ANTI-SNORING SOLUTION</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.1]" data-id="element-173">
              Sleep Together Again Since Day 1…{' '}
              <span className="text-[#1E40AF]" data-id="element-174">
                Or Keep Letting Snoring Destroy Your Relationship
              </span>
            </h1>

            <p className="text-xl md:text-2xl font-semibold text-gray-500 italic" data-id="element-175">
              Silent sleep is a luxury… and you can have it tonight.
            </p>

            <div className="space-y-4" data-id="element-176">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-medium" data-id="element-177">
                SnoreStop™ is the simple mouthpiece that helps shut down loud
                snoring FAST — so you can finally sleep in the same bed again…
                without the resentment, exhaustion, and fights.
              </p>

              <p className="text-lg text-gray-600 italic border-l-4 border-red-500 pl-4 py-1 bg-red-50 rounded-r-lg" data-id="element-178">
                <span className="font-bold text-red-700 not-italic block mb-1" data-id="element-179">
                  Because let's be real:
                </span>
                Snoring isn't "annoying." It's slowly wrecking your
                relationship. If your partner is losing sleep because of you…
                you're both paying the price.
              </p>
            </div>

            {/* Bullet Benefits */}
            <ul className="space-y-3 pt-2" data-id="element-180">
              {['Experience silence from night one', 'Noticeable reduction in just 1–3 nights', 'Deep, uninterrupted sleep within 7 days', 'Wake up rested — calm, sharp, and present', 'Sleep beside her again, not in separate rooms', 'End the nightly tension for good'].map((benefit, index) => <li key={index} className="flex items-start space-x-3" data-id="element-181">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5 shadow-sm" data-id="element-182">
                    <Check className="w-4 h-4 text-green-700 stroke-[3px]" data-id="element-183" />
                  </div>
                  <span className="text-gray-800 font-semibold" data-id="element-184">{benefit}</span>
                </li>)}
            </ul>

            {/* Status Dream Section */}
            <div className="bg-gray-900 text-white rounded-2xl p-6 md:p-8 shadow-xl relative overflow-hidden" data-id="element-185">
              <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600 rounded-full blur-3xl opacity-15 transform translate-x-1/2 -translate-y-1/2" data-id="element-186"></div>
              <h3 className="text-xl md:text-2xl font-extrabold mb-4" data-id="element-187">
                The Life You Get Back
              </h3>
              <p className="text-gray-400 font-medium mb-4 italic" data-id="element-188">
                Imagine this:
              </p>
              <ul className="space-y-2.5 mb-6" data-id="element-189">
                {['You wake up next to her again', 'No tension', 'No embarrassment', 'No separate rooms', 'No jokes about your snoring', 'No shame on trips'].map((line, i) => <li key={i} className="flex items-center space-x-3" data-id="element-190">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" data-id="element-191"></div>
                    <span className="text-gray-200 font-medium" data-id="element-192">{line}</span>
                  </li>)}
              </ul>
              <p className="text-lg font-bold text-white leading-relaxed" data-id="element-193">
                Just quiet sleep…{' '}
                <span className="text-blue-400" data-id="element-194">
                  like a man who handles his business.
                </span>
              </p>
              <p className="mt-3 text-gray-400 font-semibold" data-id="element-195">
                That's what SnoreStop™ really gives you.
              </p>
            </div>

            {/* CTA Block */}
            <div className="bg-[#1E40AF] rounded-2xl p-6 md:p-8 text-center shadow-2xl shadow-blue-900/40" data-id="element-196">
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2" data-id="element-197">
                Get SnoreStop™ Tonight
              </h3>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-blue-200 font-medium mb-6" data-id="element-198">
                <span className="flex items-center" data-id="element-199">
                  <Star className="w-4 h-4 mr-1 fill-current text-orange-400" data-id="element-200" />{' '}
                  Rated 4.8/5
                </span>
                <span data-id="element-201">Trusted by 10,000+ Couples</span>
                <span data-id="element-202">90-Day Money-Back Guarantee</span>
                <span data-id="element-203">Secure Checkout • Fast Shipping</span>
              </div>
              <Button onClick={scrollToPricing} variant="secondary" size="xl" className="w-full sm:w-auto shadow-xl shadow-orange-500/30 text-lg px-10 py-4 transform hover:scale-105 transition-all" data-id="element-204">
                Get SnoreStop™ Now →
              </Button>
            </div>

            {/* Trust Bar */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium text-gray-600 pt-2 border-t border-gray-100 mt-4" data-id="element-205">
              <span className="flex items-center text-orange-600 font-bold" data-id="element-206">
                <Star className="w-4 h-4 mr-1 fill-current" data-id="element-207" /> Rated 4.8/5
              </span>
              <span className="flex items-center" data-id="element-208">
                <Check className="w-4 h-4 mr-1 text-green-600" data-id="element-209" /> Trusted by
                10,000+ Couples
              </span>
              <span className="flex items-center" data-id="element-210">
                <Check className="w-4 h-4 mr-1 text-green-600" data-id="element-211" /> 90-Day
                Money-Back Guarantee
              </span>
              <span className="flex items-center" data-id="element-212">
                <Check className="w-4 h-4 mr-1 text-green-600" data-id="element-213" /> Secure
                Checkout
              </span>
              <span className="flex items-center" data-id="element-214">
                <Check className="w-4 h-4 mr-1 text-green-600" data-id="element-215" /> Fast Shipping
              </span>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-216">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-217">
              <img src={IMAGES.hero} alt="Couple sleeping peacefully" className="w-full h-auto object-cover" data-id="element-218" />

              {/* Floating Badge */}
              <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-xl max-w-[180px] border border-gray-100" data-id="element-219">
                <div className="flex text-orange-500 mb-1.5" data-id="element-220">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-221" />)}
                </div>
                <p className="text-sm font-bold text-gray-900 leading-tight" data-id="element-222">
                  "Saved our marriage in 3 days!"
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-223">
                  — Sarah M., Verified Buyer
                </p>
              </div>
            </div>

            {/* Additional visual element - "Before/After" hint */}
            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-gray-100 hidden md:block max-w-[200px]" data-id="element-224">
              <div className="flex items-center space-x-3 mb-2" data-id="element-225">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-226"></div>
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-227">
                  Status
                </span>
              </div>
              <p className="font-bold text-gray-900" data-id="element-228">
                Sleeping in the same bed again ❤️
              </p>
            </div>
          </motion.div>
        </div>

        {/* Hero Social Proof */}
        <div className="mt-16 flex flex-col items-center justify-center text-center space-y-4 border-t border-gray-100 pt-8" data-id="element-229">
          <p className="text-sm font-bold text-gray-400 uppercase tracking-widest" data-id="element-230">
            Join 10,000+ Happy Couples
          </p>
          <div className="flex -space-x-3" data-id="element-231">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="w-12 h-12 rounded-full border-2 border-white bg-gray-200 overflow-hidden shadow-sm" data-id="element-232">
                <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-233" />
              </div>)}
          </div>
        </div>
      </Container>
    </section>;
}